import { memo } from 'react';
import { Box, alpha } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FlagIcon from '@mui/icons-material/Flag';
import { BrandColors } from '@neumind/shared/branding';
import { PhoneNotification } from '../ui/PhoneNotification';
import { NotificationFadeIn } from '../ui/NotificationFadeIn';

// IDEA-260721-rpva item 1: the floating screenshot cards read "a bit tacky" in
// the BABICM walkthrough. Replaced with coded phone-OS-style notifications
// (frosted glass, app chip, timestamp) that pop in staggered, over a stronger
// colour field behind the cut-out hero person.
const NOTIFICATIONS = [
    {
        icon: <BoltIcon />,
        title: 'Fatigue check-in',
        body: "How's your energy this afternoon?",
        time: 'now',
        accentColor: BrandColors.warning.main,
        delay: 400,
        floatDelay: '0s',
        position: { top: { xs: '2%', md: '4%' }, left: { xs: '-2%', md: '2%' } },
    },
    {
        icon: <ChecklistIcon />,
        title: 'Morning routine',
        body: '3 of 4 steps done',
        time: '9:30',
        accentColor: BrandColors.primary.main,
        delay: 800,
        floatDelay: '1s',
        position: { top: { xs: '56%', md: '58%' }, left: { xs: '-4%', md: '-4%' } },
    },
    {
        icon: <FlagIcon />,
        title: 'Goal progress',
        body: 'Make lunch — step completed',
        time: '12:15',
        accentColor: BrandColors.success.main,
        delay: 1200,
        floatDelay: '2s',
        position: { bottom: { xs: '14%', md: '18%' }, right: { xs: '-4%', md: '-4%' } },
    },
] as const;

function HeroMockupComponent() {
    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 350, md: 450 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
            }}
        >
            {/* Colour field behind the hero person (rpva: "stronger/better
                background colour behind the hero person") — a soft brand
                gradient panel, flat at the base so the cut-out figure sits on
                it rather than floating on the page background.
                VER-260805 J1.2: extended to the whole hero container — the
                confined panel read as a rectangle around the image. */}
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '32px 32px 24px 24px',
                    background: `linear-gradient(165deg, ${alpha(BrandColors.primary.main, 0.16)} 0%, ${alpha(
                        BrandColors.secondary.main,
                        0.1,
                    )} 55%, ${alpha(BrandColors.primary.main, 0.06)} 100%)`,
                    zIndex: 0,
                }}
            />

            {/* Hero person */}
            <Box
                component="img"
                src="/images/hero/hero-person.webp"
                alt="Person using neumind"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                sx={{
                    position: 'relative',
                    height: '100%',
                    maxHeight: { xs: 350, md: 450 },
                    width: 'auto',
                    objectFit: 'contain',
                    objectPosition: 'bottom',
                    zIndex: 1,
                }}
            />

            {/* Phone-OS-style notifications. Decorative composition for
                sighted users — announced once as a single image. */}
            <Box
                role="img"
                aria-label="Examples of neumind app notifications: a fatigue check-in, a morning routine update and a completed goal step"
                sx={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}
            >
                {NOTIFICATIONS.map((n) => (
                    // Outer box owns position + the idle float animation;
                    // NotificationFadeIn owns the pop-in transform. Keeping
                    // them on separate elements stops the float `animation`
                    // overriding the entry transform.
                    <Box
                        key={n.title}
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            width: { xs: 190, sm: 230, md: 260 },
                            ...n.position,
                            animation: `float 6s ease-in-out infinite ${n.floatDelay}`,
                            '@keyframes float': {
                                '0%, 100%': { transform: 'translateY(0px)' },
                                '50%': { transform: 'translateY(-8px)' },
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                            },
                        }}
                    >
                        <NotificationFadeIn delay={n.delay} direction="up" distance={16}>
                            <PhoneNotification
                                icon={n.icon}
                                title={n.title}
                                body={n.body}
                                time={n.time}
                                accentColor={n.accentColor}
                            />
                        </NotificationFadeIn>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export const HeroMockup = memo(HeroMockupComponent);
